import type { AppContext } from '../../app';
import { DIFFICULTIES, skillFor, TIERS, TOP_TIER } from '../../ai/skills';
import { BUILDS } from '../../game/constants';
import type { KnightSpec } from '../../game/match';
import type { Role } from '../../game/types';
import { option } from '../../knight/catalog';
import { exhibitionSession } from '../../modes/exhibition';
import { HOUSES, type House } from '../../setup/houses';
import { choice } from '../choice';
import { button, h } from '../dom';
import { blockColour, crest } from '../heraldry';
import { throughTheGates } from '../motion';
import { drawPortrait } from '../portrait';

export const ROLE_NAMES: Readonly<Record<Role, string>> = {
  gk: 'Keeper',
  def: 'Defender',
  mid: 'Midfield',
  fwd: 'Forward',
};

export const stars = (tier: number): string => '★'.repeat(tier + 1) + '☆'.repeat(TIERS.length - tier - 1);

/** ◀ ★★★☆☆ Knight ▶ — steps one bot's skill tier. */
function tierPicker(spec: KnightSpec, onChange: (tier: number) => void): HTMLElement {
  const step = (by: number, label: string): HTMLButtonElement =>
    h('button', {
      text: by < 0 ? '◀' : '▶',
      attrs: {
        type: 'button',
        'aria-label': `${label} ${spec.look.name}'s level`,
        ...(spec.tier + by < 0 || spec.tier + by > TOP_TIER ? { disabled: '' } : {}),
      },
      on: {
        click: () => {
          onChange(spec.tier + by);
        },
      },
    });
  return h(
    'div',
    { class: 'tier-pick' },
    step(-1, 'Lower'),
    h(
      'small',
      { class: 'tier', attrs: { title: 'Bot skill level' } },
      h('span', { class: 'stars', text: stars(spec.tier), attrs: { 'aria-hidden': 'true' } }),
      skillFor(spec.tier).name,
    ),
    step(1, 'Raise'),
  );
}

function knightCard(spec: KnightSpec, house: House, onTier: (tier: number) => void): HTMLElement {
  const canvas = h('canvas');
  const card = h(
    'div',
    { class: `card-knight${spec.human ? ' is-you' : ''}` },
    canvas,
    h('b', { text: spec.look.name }),
    h('small', {
      text: `${spec.human ? 'You · ' : ''}${ROLE_NAMES[spec.role]} · ${option(BUILDS, spec.look.build).name}`,
    }),
    spec.human ? h('small', { class: 'tier', text: 'Controlled by you' }) : tierPicker(spec, onTier),
  );
  drawPortrait(canvas, spec.look, house.colour, { width: 110, height: 128 });
  return card;
}

export function mountSetup(root: HTMLElement, app: AppContext): () => void {
  const body = h('div', { class: 'setup-body' });

  const render = (): void => {
    const lineup = (label: string, house: House, team: 0 | 1, specs: readonly KnightSpec[]): HTMLElement =>
      h(
        'div',
        { class: 'lineup panel' },
        h(
          'h3',
          {},
          h(
            'span',
            { class: 'house-block', style: { background: blockColour(house) } },
            crest(house),
            house.name,
          ),
          label,
        ),
        h(
          'div',
          { class: 'cards', attrs: { style: `--n: ${specs.length}` } },
          ...specs.map((s, i) =>
            knightCard(s, house, (tier) => {
              app.setKnightTier(team, i, tier);
              render();
            }),
          ),
        ),
      );
    body.replaceChildren(
      h(
        'div',
        { class: 'choices panel' },
        choice(
          'Your house',
          HOUSES.map((house) => ({ value: house.id, label: house.name, colour: house.colour })),
          app.prefs.houseId,
          (houseId) => {
            app.setPrefs({ houseId });
            render();
          },
        ),
        choice(
          'Teammates',
          TIERS.map((t, tier) => ({ value: tier, label: t.name })),
          app.prefs.teammateTier,
          (teammateTier) => {
            app.setPrefs({ teammateTier });
            render();
          },
        ),
        choice(
          'Rivals',
          DIFFICULTIES.map((d) => ({ value: d.id, label: `${d.label} (mixed)` })),
          app.prefs.difficultyId,
          (difficultyId) => {
            app.setPrefs({ difficultyId });
            render();
          },
        ),
        choice(
          'Teams',
          [
            { value: 4, label: '4v4' },
            { value: 3, label: '3v3' },
          ],
          app.prefs.teamSize,
          (teamSize) => {
            app.setPrefs({ teamSize: teamSize === 3 ? 3 : 4 });
            render();
          },
        ),
      ),
      h(
        'p',
        { class: 'hint' },
        `Rivals are mixed around the ${app.difficulty.label} level, so some are better than others. Fine-tune any knight's level with the arrows on their card.`,
      ),
      h(
        'div',
        { class: 'lineups' },
        lineup('Your house', app.house, 0, app.squads.home),
        lineup('Rivals', app.rival, 1, app.squads.away),
      ),
    );
  };

  root.append(
    h(
      'section',
      { class: 'setup' },
      h(
        'header',
        { class: 'screen-head' },
        button('← Home', () => {
          app.go('home');
        }),
        h('h2', { text: 'Exhibition match' }),
      ),
      body,
      h(
        'div',
        { class: 'setup-actions' },
        button('New random squads', () => {
          app.rerollSquads();
          render();
        }),
        button('Edit my knight', () => {
          app.go('forge');
        }),
        button(
          'Kick off',
          () => {
            throughTheGates(() => {
              app.playMatch(exhibitionSession(app));
            });
          },
          'btn-primary',
        ),
      ),
    ),
  );
  render();
  return () => undefined;
}
